import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Wrench, Gauge, ShieldCheck, Handshake } from "lucide-react";
import { MaskedLines, FadeIn, Reveal } from "@/components/Reveal";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { VehicleCard } from "@/components/VehicleCard";
import { ReviewsCarousel } from "@/components/ReviewsCarousel";
import { ReviewForm } from "@/components/ReviewForm";
import { IMG, SERVICES } from "@/lib/site";
import { api } from "@/lib/api";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden" data-testid="home-hero">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-[30%]">
        <motion.img
          src={IMG.hero}
          alt="BMW M3"
          className="h-full w-full object-cover"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-black/45" />
      <div className="absolute inset-0 bg-gradient-to-t from-trx-black via-trx-black/20 to-black/50" />
      <div className="absolute inset-0 spotlight" />

      <motion.div style={{ opacity: fade }} className="relative flex min-h-screen flex-col justify-end">
        <div className="mx-auto w-full max-w-[1600px] px-6 pb-28 md:px-10 md:pb-36">
          <FadeIn delay={0.2}>
            <div className="mb-8 flex items-center gap-4">
              <span className="h-px w-14 bg-trx-blue" />
              <p className="eyebrow text-zinc-300">Statesville · Charlotte NC</p>
            </div>
          </FadeIn>

          <h1 className="font-display text-[13vw] sm:text-7xl lg:text-[7.5rem] font-black leading-[0.92] tracking-tighter text-white" data-testid="hero-headline">
            <MaskedLines lines={["Precision.", "Performance.", "Trust."]} delay={0.35} />
          </h1>

          <FadeIn delay={1.05}>
            <p className="mt-9 max-w-xl text-base md:text-lg leading-relaxed text-zinc-300" data-testid="hero-subheadline">
              TRX Auto Sales & Service Inc delivers premium vehicle sales, expert automotive repairs, advanced diagnostics,
              and performance upgrades with dealership-level quality.
            </p>
          </FadeIn>

          <FadeIn delay={1.25}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/inventory"
                data-testid="hero-view-inventory"
                className="group flex items-center gap-3 bg-trx-blue px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:bg-white hover:text-black"
              >
                View Inventory
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/service"
                data-testid="hero-schedule-service"
                className="flex items-center gap-3 border border-white/25 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white backdrop-blur-sm transition-all duration-300 hover:border-trx-blue hover:text-trx-blue"
              >
                Schedule Service
              </Link>
            </div>
          </FadeIn>
        </div>

        <FadeIn delay={1.6} className="absolute bottom-8 right-6 hidden md:block md:right-10">
          <div className="flex items-center gap-3 text-zinc-500">
            <span className="text-[10px] font-semibold uppercase tracking-[0.3em]">Scroll</span>
            <motion.span
              className="block h-10 w-px bg-gradient-to-b from-trx-blue to-transparent"
              animate={{ scaleY: [0.3, 1, 0.3] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </FadeIn>
      </motion.div>
    </section>
  );
};

const VALUES = [
  { num: "01", icon: Gauge, title: "Craftsmanship", desc: "Every vehicle is serviced and prepared to a standard, not a price point. Dealership-level process, enthusiast-level care." },
  { num: "02", icon: Handshake, title: "Honest Communication", desc: "Straight answers, transparent pricing, and no surprises. You approve everything before we turn a bolt." },
  { num: "03", icon: ShieldCheck, title: "Attention To Detail", desc: "From torque specs to paint correction, the details are where trust is built — and we never skip them." },
];

const About = () => (
  <section className="relative py-24 md:py-36" data-testid="about-section">
    <div className="mx-auto max-w-[1600px] px-6 md:px-10">
      <div className="grid items-center gap-16 lg:grid-cols-2 lg:gap-24">
        <Reveal>
          <div className="relative">
            <div className="absolute -inset-4 border border-trx-blue/20" />
            <div className="relative overflow-hidden">
              <img src={IMG.shop} alt="TRX Auto facility" className="aspect-[4/5] w-full object-cover md:aspect-[5/6]" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            </div>
            <div className="absolute -bottom-8 -right-4 md:-right-8 border border-white/10 bg-black/80 p-6 backdrop-blur-xl">
              <p className="font-display text-4xl font-black text-trx-blue">10+</p>
              <p className="mt-1 text-xs uppercase tracking-[0.2em] text-zinc-400">Years of expertise</p>
            </div>
          </div>
        </Reveal>

        <div>
          <Reveal>
            <p className="eyebrow mb-6 text-trx-blue">About TRX</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tighter text-white">
              Driven By Passion.<br />Built On Trust.
            </h2>
            <p className="mt-8 max-w-lg text-base leading-relaxed text-zinc-400">
              At TRX Auto Sales & Service Inc, we believe every customer deserves a premium automotive experience. Whether
              you are purchasing your next vehicle, maintaining your daily driver, or building a performance machine,
              our team provides expert craftsmanship, honest communication, and attention to detail.
            </p>
          </Reveal>

          <div className="mt-12 space-y-0 border-t border-white/10">
            {VALUES.map((v, i) => (
              <Reveal key={v.num} delay={i * 0.1}>
                <div className="group flex gap-6 border-b border-white/10 py-7 transition-colors duration-300 hover:border-trx-blue/40" data-testid={`value-${v.num}`}>
                  <span className="font-display text-sm font-bold text-trx-blue">{v.num}</span>
                  <div>
                    <h3 className="flex items-center gap-3 font-display text-xl font-bold tracking-tight text-white">
                      <v.icon className="h-5 w-5 text-trx-blue" /> {v.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-zinc-500">{v.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Services = () => (
  <section className="relative border-t border-white/10 py-24 md:py-36" data-testid="services-section">
    <div className="mx-auto max-w-[1600px] px-6 md:px-10">
      <div className="mb-16 flex flex-col gap-6 md:mb-20 md:flex-row md:items-end md:justify-between">
        <Reveal>
          <p className="eyebrow mb-6 text-trx-blue">What We Do</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tighter text-white">
            Service Without<br />Compromise.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <Link to="/service" data-testid="services-book-link" className="group flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:text-trx-blue">
            Book an appointment <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </Reveal>
      </div>

      <div className="grid gap-px bg-white/10 md:grid-cols-2">
        {SERVICES.map((s, i) => (
          <Reveal key={s.num} delay={(i % 2) * 0.1} y={30}>
            <div className="group relative h-full overflow-hidden bg-trx-black p-8 md:p-12" data-testid={`service-card-${s.num}`}>
              <img
                src={s.image}
                alt={s.title}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover opacity-0 transition-all duration-700 group-hover:opacity-[0.14] group-hover:scale-105"
              />
              <div className="relative">
                <div className="flex items-start justify-between">
                  <span className="font-display text-6xl md:text-7xl font-black leading-none text-white/[0.07] transition-colors duration-500 group-hover:text-trx-blue/25">
                    {s.num}
                  </span>
                  <Wrench className="h-5 w-5 text-zinc-600 transition-colors duration-300 group-hover:text-trx-blue" />
                </div>
                <h3 className="mt-8 font-display text-2xl md:text-3xl font-bold tracking-tight text-white">{s.title}</h3>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-zinc-500">{s.desc}</p>
                <ul className="mt-7 flex flex-wrap gap-2">
                  {s.items.map((item) => (
                    <li key={item} className="border border-white/10 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-zinc-400 transition-colors duration-300 group-hover:border-white/20">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Featured = () => {
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    api.get("/vehicles", { params: { featured: true } }).then((r) => setVehicles(r.data.slice(0, 3))).catch(() => {});
  }, []);

  if (!vehicles.length) return null;

  return (
    <section className="relative border-t border-white/10 py-24 md:py-36" data-testid="featured-inventory">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <Reveal>
            <p className="eyebrow mb-6 text-trx-blue">Featured Inventory</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tighter text-white">
              Quality Vehicles.<br />Carefully Selected.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/inventory" data-testid="featured-view-all" className="group flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:text-trx-blue">
              View all inventory <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </Reveal>
        </div>
        <div className="grid gap-8 md:grid-cols-3">
          {vehicles.map((v, i) => (
            <VehicleCard key={v.id} vehicle={v} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

const PerformanceBand = () => (
  <section className="relative overflow-hidden border-t border-white/10" data-testid="performance-band">
    <div className="absolute inset-0">
      <img src={IMG.engineBay} alt="Performance engine bay" className="h-full w-full object-cover" />
      <div className="absolute inset-0 bg-black/70" />
    </div>
    <div className="relative mx-auto flex max-w-[1600px] flex-col items-start gap-8 px-6 py-28 md:px-10 md:py-40">
      <Reveal>
        <p className="eyebrow text-trx-blue">TRX Performance</p>
        <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-7xl font-black leading-[0.95] tracking-tighter text-white">
          Built For<br />Performance.
        </h2>
        <p className="mt-6 max-w-lg text-base leading-relaxed text-zinc-400">
          Downpipes, turbo upgrades, exhaust systems, coilovers, and full custom builds — engineered for European
          platforms, muscle cars, and everything with boost.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <Link
          to="/performance"
          data-testid="performance-band-cta"
          className="group flex items-center gap-3 bg-white px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-black transition-all duration-300 hover:bg-trx-blue hover:text-white"
        >
          Explore Performance <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const Reviews = () => (
  <section className="relative border-t border-white/10 py-24 md:py-36" data-testid="reviews-section">
    <div className="mx-auto max-w-[1600px] px-6 md:px-10">
      <div className="grid gap-14 lg:grid-cols-5 lg:gap-24">
        <Reveal className="lg:col-span-2">
          <p className="eyebrow mb-6 text-trx-blue">Client Reviews</p>
          <h2 className="font-display text-4xl sm:text-5xl font-black leading-[0.95] tracking-tighter text-white">
            Trusted With The Cars People Love.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-zinc-500">
            From daily drivers to six-figure exotics, our customers trust us with the vehicles that matter most.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-3">
          <ReviewsCarousel />
        </Reveal>
      </div>
      <Reveal delay={0.2} className="mt-14">
        <ReviewForm />
      </Reveal>
    </div>
  </section>
);

const CTA = () => (
  <section className="relative overflow-hidden border-t border-white/10 py-24 md:py-36" data-testid="home-cta">
    <div className="absolute inset-0 spotlight" />
    <div className="relative mx-auto max-w-[1600px] px-6 md:px-10">
      <Reveal>
        <h2 className="max-w-4xl font-display text-4xl sm:text-5xl lg:text-7xl font-black leading-[0.95] tracking-tighter text-white">
          Ready To Upgrade Your Automotive Experience?
        </h2>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="mt-12 flex flex-wrap gap-4">
          <Link
            to="/contact"
            data-testid="cta-contact"
            className="group flex items-center gap-3 bg-trx-blue px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:bg-white hover:text-black"
          >
            Contact Our Team <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <a
            href="tel:+17043804555"
            data-testid="cta-call"
            className="flex items-center gap-3 border border-white/25 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:border-trx-blue hover:text-trx-blue"
          >
            Call (704) 380-4555
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  useEffect(() => {
    document.title = "TRX Auto Sales & Service Inc | Used Cars, Auto Repair & Performance — Charlotte & Statesville NC";
  }, []);

  return (
    <>
      <Hero />
      <EditorialMarquee />
      <About />
      <Services />
      <Featured />
      <PerformanceBand />
      <Reviews />
      <CTA />
    </>
  );
}
